// useCouponReservationStore.js
// Store Pinia para o fluxo de reservas de cupom (coupon_reservations).
// Espelha o padrão de useCouponStore.js e useSubscriptionStore.js.

import { defineStore } from "pinia";
import * as couponReservationService from "@/services/couponReservationService";

export const useCouponReservationStore = defineStore("couponReservation", {
  // ─────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────
  state: () => ({
    reservations: [],
    selectedReservationIds: [],
    selectedReservation: null,
    error: null,

    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,

    // Força o próprio userId para não-Sys (padrão useSubscriptionStore)
    userIdFilter: null,

    shouldForcePageOne: false,

    filters: {
      couponId: "", // substring numérica
      userId: "", // igualdade exata (travado para não-Sys)
      subscriptionId: "", // substring numérica (nullable)
      couponCode: "", // substring textual (LIKE)
      userName: "", // substring textual (LIKE)
      reservationStatus: "", // "" | "active" | "consumed" | "cancelled" | "expired"
    },
  }),

  // ─────────────────────────────────────────────────────────────────────────
  // GETTERS
  // ─────────────────────────────────────────────────────────────────────────
  getters: {
    isAllSelected: (state) => (total) =>
      state.selectedReservationIds.length === total,

    /**
     * Filtragem client-side idêntica ao backend, como espelho visual enquanto
     * o debounce está em voo. O campo `reservationStatus` já vem computado do
     * backend (consumed > cancelled > expired > active), então aqui é só
     * igualdade exata.
     */
    filteredReservations: (state) => {
      const f = state.filters;

      return state.reservations.filter((r) => {
        // ── couponId ──────────────────────────────────────────────────
        if (f.couponId.trim() !== "") {
          if (!String(r.couponId ?? "").includes(f.couponId.trim()))
            return false;
        }

        // ── userId (igualdade exata) ──────────────────────────────────
        if (f.userId.trim() !== "") {
          if (String(r.userId ?? "") !== f.userId.trim()) return false;
        }

        // ── subscriptionId (substring numérica sobre campo nullable) ──
        if (f.subscriptionId.trim() !== "") {
          if (r.subscriptionId == null) return false;
          if (!String(r.subscriptionId).includes(f.subscriptionId.trim()))
            return false;
        }

        // ── couponCode ────────────────────────────────────────────────
        if (f.couponCode.trim() !== "") {
          const code = (r.couponCode ?? "").toLowerCase();
          if (!code.includes(f.couponCode.trim().toLowerCase())) return false;
        }

        // ── userName ──────────────────────────────────────────────────
        if (f.userName.trim() !== "") {
          const name = (r.userName ?? "").toLowerCase();
          if (!name.includes(f.userName.trim().toLowerCase())) return false;
        }

        // ── reservationStatus (campo computado no backend) ────────────
        if (f.reservationStatus.trim() !== "") {
          const rs = (r.reservationStatus ?? "").toLowerCase();
          if (rs !== f.reservationStatus.trim().toLowerCase()) return false;
        }

        return true;
      });
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────
  actions: {
    // ── Seleção ──────────────────────────────────────────────────────────
    toggleSelection(id) {
      const index = this.selectedReservationIds.indexOf(id);
      if (index > -1) {
        this.selectedReservationIds.splice(index, 1);
      } else {
        this.selectedReservationIds.push(id);
      }
    },

    selectAll(ids) {
      this.selectedReservationIds = [...ids];
    },

    clearSelection() {
      this.selectedReservationIds = [];
    },

    // ── Fetch paginado ────────────────────────────────────────────────────
    async fetchCouponReservationsPaged(
      pageNumber,
      pageSize,
      legacyUserIdFilter,
    ) {
      try {
        const filters = {
          couponId: this.filters.couponId,
          userId:
            this.filters.userId ||
            (legacyUserIdFilter != null ? String(legacyUserIdFilter) : ""),
          subscriptionId: this.filters.subscriptionId,
          couponCode: this.filters.couponCode,
          userName: this.filters.userName,
          reservationStatus: this.filters.reservationStatus,
        };

        const response =
          await couponReservationService.fetchCouponReservationsPaged(
            pageNumber,
            pageSize,
            filters,
          );

        const payload = response.data ?? {};
        this.reservations = payload.data ?? [];
        this.totalCount = payload.totalCount ?? 0;
        this.totalPages = payload.totalPages ?? 1;
        this.currentPage = pageNumber;
        this.error = null;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.error = { message };
        console.error(
          "❌ Erro ao buscar coupon_reservations paginadas:",
          message,
        );
        throw new Error(message);
      }
    },

    // ── setUserIdFilter ──────────────────────────────────────────────────
    setUserIdFilter(userId) {
      this.userIdFilter = userId;
      this.filters.userId = userId != null ? String(userId) : "";
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchCouponReservationsPaged(1, this.pageSize, userId);
    },

    // ── setFilter ─────────────────────────────────────────────────────────
    setFilter(field, value) {
      if (!(field in this.filters)) return;
      this.filters[field] = value;

      if (field === "userId") {
        const trimmed = String(value ?? "").trim();
        const parsed = trimmed === "" ? null : Number(trimmed);
        const isValidInt =
          trimmed === "" || (Number.isInteger(parsed) && parsed > 0);
        if (isValidInt) {
          this.setUserIdFilter(parsed);
        }
        return;
      }

      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchCouponReservationsPaged(1, this.pageSize, this.userIdFilter);
    },

    // ── clearFilters ──────────────────────────────────────────────────────
    clearFilters(shouldRefetch = true) {
      this.filters = {
        couponId: "",
        userId: "",
        subscriptionId: "",
        couponCode: "",
        userName: "",
        reservationStatus: "",
      };

      this.userIdFilter = null;
      this.currentPage = 1;
      this.shouldForcePageOne = true;

      if (shouldRefetch) {
        this.fetchCouponReservationsPaged(1, this.pageSize, null);
      }
    },

    // ── Busca por ID ──────────────────────────────────────────────────────
    async getCouponReservationById(id) {
      try {
        const response =
          await couponReservationService.getCouponReservationById(id);
        this.selectedReservation = response.data;
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao buscar coupon_reservation por ID:", message);
        throw new Error(message);
      }
    },

    // ── Criação ───────────────────────────────────────────────────────────
    async createCouponReservation(data) {
      try {
        const response = await couponReservationService.createCouponReservation(
          data,
        );
        this.shouldForcePageOne = true;
        await this.fetchCouponReservationsPaged(
          1,
          this.pageSize,
          this.userIdFilter,
        );
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao criar coupon_reservation:", message);
        throw error;
      }
    },

    // ── Atualização ───────────────────────────────────────────────────────
    async updateCouponReservation(data) {
      try {
        await couponReservationService.updateCouponReservation(data);
        await this.fetchCouponReservationsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao atualizar coupon_reservation:", message);
        throw new Error(message);
      }
    },

    // ── Delete unitário ───────────────────────────────────────────────────
    async deleteCouponReservationById(id) {
      try {
        await couponReservationService.deleteCouponReservation(id);
        await this.fetchCouponReservationsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        if (this.reservations.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchCouponReservationsPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir coupon_reservation:", message);
        throw new Error(message);
      }
    },

    // ── Delete em lote (seleção da store) ────────────────────────────────
    async deleteSelectedCouponReservations() {
      if (!this.selectedReservationIds.length) return;

      try {
        await couponReservationService.deleteCouponReservationsBatch(
          this.selectedReservationIds,
        );
        this.clearSelection();
        await this.fetchCouponReservationsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        if (this.reservations.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchCouponReservationsPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error(
          "❌ Erro ao excluir coupon_reservations em lote:",
          message,
        );
        throw new Error(message);
      }
    },

    // ── Delete em lote por IDs externos ───────────────────────────────────
    async deleteCouponReservationsByIds(ids) {
      if (!ids || !ids.length) return;
      try {
        await couponReservationService.deleteCouponReservationsBatch(ids);
        this.selectedReservationIds = this.selectedReservationIds.filter(
          (id) => !ids.includes(id),
        );
        await this.fetchCouponReservationsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        if (this.reservations.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchCouponReservationsPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error(
          "❌ Erro ao excluir coupon_reservations em lote (by ids):",
          message,
        );
        throw new Error(message);
      }
    },
  },
});
